import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore } from '../stores/userStore';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Send, Sparkles, MessageCircle, HelpCircle, Menu, X, Plus, Clock, Trash2, ChevronLeft, Mic, MicOff, Settings } from 'lucide-react';
import StarAnimation from '../components/StarAnimation';
import { chatPersistenceService, ChatMessage } from '../services/chatPersistenceService';
import { chartManagementService, LLMContextResponse } from '../services/chartManagementService';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL_WITH_VERSION } from '../config/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface PersonalChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
  isExpanded?: boolean;
  metadata?: {
    category?: 'personality' | 'career' | 'relationships' | 'health' | 'spiritual' | 'general';
    confidence?: number;
    tags?: string[];
    brief_answer?: string;
    detailed_description?: string;
    sources?: string[];
  };
}

interface Conversation {
  conversationId: string;
  title: string;
  metadata?: {
    type?: string;
  };
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
}

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  prompt: string;
  category: 'personality' | 'career' | 'relationships' | 'health' | 'spiritual' | 'general';
}

// Helper function to render markdown content
const renderMarkdownContent = (content: string) => {
  return (
    <div className="prose-mobile">
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={{
          // Mobile-optimized styling with reduced spacing
          h1: ({ children }) => <h1 className="text-lg sm:text-xl font-bold text-purple-200">{children}</h1>,
          h2: ({ children }) => <h2 className="text-base sm:text-lg font-semibold text-purple-200">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm sm:text-base font-medium text-purple-200">{children}</h3>,
          p: ({ children }) => <p className="text-sm sm:text-base">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-purple-200">{children}</strong>,
          em: ({ children }) => <em className="italic text-purple-300">{children}</em>,
          ul: ({ children }) => <ul className="list-disc list-inside ml-2">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside ml-2">{children}</ol>,
          li: ({ children }) => <li className="text-sm sm:text-base">{children}</li>,
          blockquote: ({ children }) => <blockquote className="italic text-purple-300 text-sm sm:text-base">{children}</blockquote>,
          code: ({ children }) => <code className="bg-purple-900/50 rounded font-mono">{children}</code>,
          pre: ({ children }) => <pre className="bg-purple-900/50 rounded-lg overflow-x-auto">{children}</pre>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

const PersonalChat: React.FC = () => {
  const { user: authUser } = useAuth();
  const { user: storeUser, syncWithAuth, updateUser } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Use storeUser if available, otherwise use authUser
  const user = storeUser || authUser;
  
  const [messages, setMessages] = useState<PersonalChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'chat' | 'history'>('chat');
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isRestoringConversation, setIsRestoringConversation] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const [hasSeenWelcomeMessage, setHasSeenWelcomeMessage] = useState(false);
  const [isStartingNewChat, setIsStartingNewChat] = useState(false);
  const [lastMessageClearTime, setLastMessageClearTime] = useState(0);
  
  // New enhanced features
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [isVoiceInputActive, setIsVoiceInputActive] = useState(false);
  const [showMessageActions, setShowMessageActions] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [messageFontSize, setMessageFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  
  // Chart management state
  const [userCharts, setUserCharts] = useState<LLMContextResponse | null>(null);
  const [isLoadingCharts, setIsLoadingCharts] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const previousMessageCountRef = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const voiceRecognitionRef = useRef<any>(null);
  const newChatRef = useRef(false); // Track new chat state more reliably

  // Load user charts for enhanced chat context
  const loadUserCharts = async (userId: string) => {
    if (!userId) return;
    
    setIsLoadingCharts(true);
    setChartError(null);
    
    try {
      const chartSummary = await chartManagementService.getChartSummary(userId);
      if (chartSummary.success) {
        console.log('✅ User charts loaded:', chartSummary.data);
      } else {
        console.warn('⚠️ No charts found for user');
      }
    } catch (error) {
      console.error('❌ Error loading user charts:', error);
      setChartError('Failed to load user charts');
    } finally {
      setIsLoadingCharts(false);
    }
  };

  // Load charts when user changes
  useEffect(() => {
    if (user?.uid) {
      loadUserCharts(user.uid);
    }
  }, [user?.uid]);

  // Quick Action Buttons for better UX
  const quickActions: QuickAction[] = [
    {
      id: 'personality',
      title: 'Personality Insights',
      description: 'Discover your core traits and strengths',
      icon: '⭐',
      prompt: "What does my birth chart reveal about my personality? What are my core strengths and unique characteristics?",
      category: 'personality'
    },
    {
      id: 'career',
      title: 'Career Guidance',
      description: 'Find your ideal professional path',
      icon: '📈',
      prompt: "Which career path is best for my astrological profile? What are my professional strengths and ideal work environments?",
      category: 'career'
    },
    {
      id: 'relationships',
      title: 'Love & Relationships',
      description: 'Understand compatibility and love',
      icon: '❤️',
      prompt: "Tell me about my love life based on my chart. What are my relationship patterns and how can I improve them?",
      category: 'relationships'
    },
    {
      id: 'health',
      title: 'Health & Wellness',
      description: 'Astrological insights for well-being',
      icon: '⚡',
      prompt: "What does my birth chart say about my health? What wellness practices would benefit me most?",
      category: 'health'
    },
    {
      id: 'spiritual',
      title: 'Spiritual Growth',
      description: 'Discover your spiritual path',
      icon: '🌙',
      prompt: "What is my spiritual path according to astrology? How can I deepen my spiritual connection?",
      category: 'spiritual'
    },
    {
      id: 'current',
      title: 'Current Influences',
      description: 'Today\'s planetary guidance',
      icon: '☀️',
      prompt: "How do current planetary transits affect me? What should I focus on right now?",
      category: 'general'
    }
  ];

  // Auto-scroll to bottom only for truly new messages
  useEffect(() => {
    const currentMessageCount = messages.length;
    const previousMessageCount = previousMessageCountRef.current;
    
    // Debug logging
    console.log('Auto-scroll check:', {
      currentMessageCount,
      previousMessageCount,
      isRestoringConversation,
      isLoadingConversation,
      shouldAutoScroll: !isRestoringConversation && !isLoadingConversation && currentMessageCount > previousMessageCount && currentMessageCount > 0
    });
    
    // Only auto-scroll if:
    // 1. We're not restoring a conversation
    // 2. We're not loading a conversation
    // 3. We actually added new messages (not just loaded existing ones)
    // 4. We have messages to scroll to
    if (!isRestoringConversation && 
        !isLoadingConversation &&
        currentMessageCount > previousMessageCount && 
        currentMessageCount > 0) {
      console.log('Auto-scrolling to bottom');
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Update the previous count for next comparison
    previousMessageCountRef.current = currentMessageCount;
  }, [messages, isRestoringConversation, isLoadingConversation]);

  // Save scroll position when scrolling (debounced to prevent loops)
  useEffect(() => {
    const handleScroll = () => {
      // Don't track scroll position if we're restoring it
      if (isRestoringScroll) return;
      
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce scroll position updates
      scrollTimeoutRef.current = setTimeout(() => {
        const chatContainer = document.querySelector('.chat-messages-container');
        if (chatContainer) {
          setScrollPosition(chatContainer.scrollTop);
        }
      }, 100); // 100ms debounce
    };

    const chatContainer = document.querySelector('.chat-messages-container');
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => {
        chatContainer.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [isRestoringScroll]);

  // Restore scroll position after messages load
  useEffect(() => {
    if (messages.length > 0 && scrollPosition > 0) {
      const chatContainer = document.querySelector('.chat-messages-container');
      if (chatContainer) {
        // Set flag to prevent scroll tracking during restoration
        setIsRestoringScroll(true);
        
        // Use a longer delay to ensure DOM is fully rendered
        setTimeout(() => {
          chatContainer.scrollTop = scrollPosition;
          
          // Clear the scroll position after restoration to allow future auto-scroll
          setScrollPosition(0);
          setIsRestoringConversation(false);
          
          // Allow scroll tracking again after a short delay
          setTimeout(() => {
            setIsRestoringScroll(false);
          }, 50);
        }, 200);
      }
    }
  }, [messages, scrollPosition]);

  // Load conversations when needed
  useEffect(() => {
    if (viewMode === 'history' && user?.uid) {
      loadConversations();
    }
  }, [viewMode, user?.uid]);

  // Initialize conversation state from URL and localStorage
  useEffect(() => {
    if (user?.uid) {
      // Check URL for conversation ID first
      const urlConversationId = searchParams.get('conversation');
      const urlViewMode = searchParams.get('view') as 'chat' | 'history' || 'chat';
      
      // Check localStorage for saved state
      const savedState = localStorage.getItem(`chatState_${user.uid}`);
      let savedConversationId = null;
      let savedViewMode: 'chat' | 'history' = 'chat';
      let savedScrollPosition = 0;
      
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          savedConversationId = parsed.conversationId;
          savedViewMode = parsed.viewMode || 'chat';
          savedScrollPosition = parsed.scrollPosition || 0;
        } catch (error) {
          console.error('Error parsing saved chat state:', error);
        }
      }
      
      // Priority: URL > localStorage > default
      const finalConversationId = urlConversationId || savedConversationId;
      const finalViewMode = urlViewMode || savedViewMode;
      
      setCurrentConversationId(finalConversationId);
      setViewMode(finalViewMode);
      setScrollPosition(savedScrollPosition);
      
      // IMPORTANT: Reset isStartingNewChat flag on page load/refresh
      // This ensures that existing conversations can be loaded
      setIsStartingNewChat(false);
      
      // Update URL if needed
      if (finalConversationId && !urlConversationId) {
        setSearchParams({ conversation: finalConversationId, view: finalViewMode });
      } else if (!finalConversationId && urlConversationId) {
        setSearchParams({ view: finalViewMode });
      }
      
      // If we have a conversation ID from URL, mark that we're not starting a new chat
      // This ensures the chat history loading logic works properly
      if (finalConversationId) {
        console.log('🔄 Page loaded with existing conversation ID:', finalConversationId);
        setIsStartingNewChat(false);
      }
    }
  }, [user?.uid, searchParams, setSearchParams]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (user?.uid) {
      const stateToSave = {
        conversationId: currentConversationId,
        viewMode,
        scrollPosition,
        timestamp: Date.now()
      };
      localStorage.setItem(`chatState_${user.uid}`, JSON.stringify(stateToSave));
    }
  }, [currentConversationId, viewMode, scrollPosition, user?.uid]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlConversationId = searchParams.get('conversation');
      const urlViewMode = searchParams.get('view') as 'chat' | 'history' || 'chat';
      
      setCurrentConversationId(urlConversationId);
      setViewMode(urlViewMode);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [searchParams]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Add welcome message on first load
  useEffect(() => {
    console.log('🔄 Chat history useEffect triggered:', {
      isStartingNewChat,
      currentConversationId,
      userUid: !!user?.uid,
      messagesLength: messages.length,
      timeSinceClear: Date.now() - lastMessageClearTime,
      isPageRefresh: !isStartingNewChat && messages.length === 0 && !currentConversationId
    });
    
    // Only skip if we're explicitly starting a new chat
    if (isStartingNewChat || newChatRef.current) {
      console.log('🚫 Chat history useEffect COMPLETELY DISABLED - starting new chat');
      return;
    }
    
    // ADDITIONAL PROTECTION: Check URL parameters directly
    const urlParams = new URLSearchParams(window.location.search);
    const urlConversationId = urlParams.get('conversation');
    if (urlConversationId && (isStartingNewChat || newChatRef.current)) {
      console.log('🚫 URL still has conversation ID - forcing new chat mode');
      return;
    }
    
    // Don't skip on page refresh - we need to load existing conversations
    const isPageRefresh = !isStartingNewChat && messages.length === 0 && !currentConversationId;
    if (isPageRefresh) {
      console.log('🔄 Page refresh detected - will load existing conversations');
    }
    
    const loadChatHistory = async () => {
      if (user?.uid) {
        try {
          if (currentConversationId) {
            // Load specific conversation
            console.log('📂 Loading specific conversation:', currentConversationId);
            setIsLoadingConversation(true);
            setIsRestoringConversation(true);
            const dbMessages = await chatPersistenceService.loadChatHistory(50, currentConversationId);
            if (dbMessages.length > 0) {
              setMessages(dbMessages);
              setIsLoadingConversation(false);
              return;
            }
          } else {
            // Load recent chat history or most recent conversation
            console.log('📚 Loading recent chat history or most recent conversation');
            
            // First try to get recent conversations to find the most recent one
            const recentConversations = await chatPersistenceService.getRecentConversations(5);
            if (recentConversations && recentConversations.length > 0) {
              // Get the most recent conversation
              const mostRecentConversation = recentConversations[0];
              console.log('📂 Found most recent conversation:', mostRecentConversation.id);
              
              // Set the conversation ID
              setCurrentConversationId(mostRecentConversation.id);
              
              // Load messages for this conversation
              const dbMessages = await chatPersistenceService.loadChatHistory(50, mostRecentConversation.id);
              if (dbMessages.length > 0) {
                setMessages(dbMessages);
                // Update URL to reflect the loaded conversation
                setSearchParams({ conversation: mostRecentConversation.id, view: 'chat' });
                return;
              }
            } else {
              // Try to load any recent messages without conversation ID
              const dbMessages = await chatPersistenceService.loadChatHistory(50);
              if (dbMessages.length > 0) {
                setMessages(dbMessages);
                return;
              }
            }
          }
          
          // If no database messages, show welcome message
          setIsRestoringConversation(false);
          setIsLoadingConversation(false);
          showWelcomeMessage();
        } catch (error) {
          console.error('Error loading chat history:', error);
          setIsRestoringConversation(false);
          setIsLoadingConversation(false);
          showWelcomeMessage();
        }
      }
    };

    loadChatHistory();
  }, [user?.uid, user?.birthData, currentConversationId, isStartingNewChat, messages.length, lastMessageClearTime]);

  // Separate function for loading conversation by ID (can be called from other functions)
  const loadConversationById = async (conversationId: string) => {
    if (user?.uid) {
      try {
        console.log('📂 Loading conversation by ID:', conversationId);
        setIsLoadingConversation(true);
        setIsRestoringConversation(true);
        const dbMessages = await chatPersistenceService.loadChatHistory(50, conversationId);
        if (dbMessages.length > 0) {
          setMessages(dbMessages);
          setIsLoadingConversation(false);
          setIsRestoringConversation(false);
          return;
        }
        
        // If no messages found, show welcome message
        setIsRestoringConversation(false);
        setIsLoadingConversation(false);
        showWelcomeMessage();
      } catch (error) {
        console.error('Error loading conversation by ID:', error);
        setIsRestoringConversation(false);
        setIsLoadingConversation(false);
        showWelcomeMessage();
      }
    }
  };

  const loadConversations = async () => {
    if (!user?.uid) return;
    
    setIsLoadingConversations(true);
    try {
      const recentConversations = await chatPersistenceService.getRecentConversations(20);
      console.log('Loaded conversations:', recentConversations);
      // Ensure we always set an array, even if the service returns unexpected data
      const conversationsArray = Array.isArray(recentConversations) ? recentConversations : [];
      setConversations(conversationsArray);
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const showWelcomeMessage = () => {
    console.log('🌟 showWelcomeMessage called:', {
      hasSeenWelcomeMessage,
      isStartingNewChat
    });
    
    // Don't show welcome message if we've already seen it and we're not starting a new chat
    if (hasSeenWelcomeMessage && !isStartingNewChat) {
      console.log('🚫 Skipping welcome message - already seen and not starting new chat');
      return;
    }
    
    console.log('✅ Showing welcome message');
    const welcomeMessage: PersonalChatMessage = {
      id: 'welcome',
      type: 'system',
      content: `🌟 Welcome to RRAASI - Your Personal AI Astrologer! 🌟

          I'm here to unlock the secrets of your birth chart and guide you through life's cosmic journey.

          ✨ Ask me about:
          • Your personality & character traits
          • Career guidance & life purpose
          • Relationships & compatibility
          • Life challenges & solutions
          • Current planetary influences

          📊 Status: ${user?.birthData ? '✅ Chart Ready' : '❌ Add Birth Details'}

          Ready to discover your cosmic blueprint? Ask me anything! 🌌`,
      timestamp: new Date()
    };
    setMessages([welcomeMessage]);
    setHasSeenWelcomeMessage(true);
  };

  const handleConversationSelect = (conversationId: string | null) => {
    console.log('🔄 handleConversationSelect called with:', conversationId);
    
    setCurrentConversationId(conversationId);
    setViewMode('chat');
    setShowHelp(false);
    setIsStartingNewChat(false);
    
    // Reset scroll state for new conversation
    if (conversationId) {
      setIsLoadingConversation(true);
      setIsRestoringConversation(true);
      setIsRestoringScroll(false);
      setScrollPosition(0);
      setSearchParams({ conversation: conversationId, view: 'chat' });
      
      // Load the selected conversation
      loadConversationById(conversationId);
    } else {
      setIsRestoringConversation(false);
      setIsLoadingConversation(false);
      setIsRestoringScroll(false);
      setScrollPosition(0);
      setSearchParams({ view: 'chat' });
      
      // Start a new chat
      handleNewChat();
    }
  };

  const handleNewChat = async () => {
    console.log('🆕 handleNewChat called - starting fresh chat');
    
    try {
      // STEP 1: Set the new chat flag FIRST and keep it active
      setIsStartingNewChat(true);
      newChatRef.current = true; // Set ref for more reliable tracking
      
      // STEP 2: Get a new conversation ID from the backend
      console.log('🆕 Requesting new conversation ID from backend...');
      const response = await fetch('/api/chat/new-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to start new conversation: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🆕 Backend returned new conversation ID:', data.conversationId);
      
      // STEP 3: Set the new conversation ID immediately
      setCurrentConversationId(data.conversationId);
      
      // STEP 4: Update URL with the new conversation ID
      setSearchParams({ conversation: data.conversationId, view: 'chat' });
      const newUrl = `${window.location.pathname}?conversation=${data.conversationId}&view=chat`;
      window.history.replaceState({}, '', newUrl);
      
      // STEP 5: Clear ALL conversation state immediately
      setMessages([]);
      setViewMode('chat');
      setShowHelp(false);
      setScrollPosition(0);
      
      // STEP 6: Disable ALL restoration and loading flags
      setIsRestoringConversation(false);
      setIsLoadingConversation(false);
      setIsRestoringScroll(false);
      setHasSeenWelcomeMessage(false);
      setLastMessageClearTime(Date.now());
      
      // STEP 7: Clear ALL stored conversation data from localStorage
      if (user?.uid) {
        localStorage.removeItem(`chat_history_${user.uid}`);
        localStorage.removeItem(`chatState_${user.uid}`);
        // Clear any other potential conversation-related keys
        Object.keys(localStorage).forEach(key => {
          if (key.includes('chat') || key.includes('conversation') || key.includes('message')) {
            localStorage.removeItem(key);
          }
        });
      }
      
      // STEP 8: Force show welcome message immediately with unique ID
      const welcomeMessage: PersonalChatMessage = {
        id: `welcome_${Date.now()}_${Math.random()}`, // Completely unique ID
        type: 'system',
        content: `🌟 Welcome to RRAASI - Your Personal AI Astrologer! 🌟

                  I'm here to unlock the secrets of your birth chart and guide you through life's cosmic journey.

                  ✨ Ask me about:
                  • Your personality & character traits
                  • Career guidance & life purpose
                  • Relationships & compatibility
                  • Life challenges & solutions
                  • Current planetary influences

                  📊 Status: ${user?.birthData ? '✅ Chart Ready' : '❌ Add Birth Details'}

                  Ready to discover your cosmic blueprint? Ask me anything! 🌌`,
        timestamp: new Date()
      };
      
      // STEP 9: Set messages directly and force re-render
      setMessages([welcomeMessage]);
      
      // STEP 10: Force a small delay to ensure state updates are processed
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // STEP 11: Double-check that we still have only the welcome message
      if (messages.length > 1) {
        console.log('🔄 Force clearing messages again to ensure clean state');
        setMessages([welcomeMessage]);
      }
      
      console.log('🆕 New chat state set - isStartingNewChat:', true);
      console.log('🆕 Messages cleared and welcome message set directly');
      console.log('🆕 New conversation ID set:', data.conversationId);
      console.log('🆕 Current messages count:', messages.length);
      console.log('🆕 Current conversation ID state:', currentConversationId);
      console.log('🆕 URL parameters after new chat:', window.location.search);
      
      // STEP 12: Keep the flag active for much longer to prevent interference
      setTimeout(() => {
        setIsStartingNewChat(false);
        newChatRef.current = false; // Clear ref
        console.log('🆕 isStartingNewChat flag reset after delay');
      }, 5000); // Increased to 5 seconds for maximum protection
      
    } catch (error) {
      console.error('❌ Error starting new conversation:', error);
      toast.error('Failed to start new conversation. Please try again.');
      
      // Fallback: clear conversation ID manually
      setCurrentConversationId(null);
      setMessages([]);
      setSearchParams({ view: 'chat' });
    }
  };

  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear this conversation? This action cannot be undone.')) {
      setMessages([]);
      setCurrentConversationId(null);
      setSearchParams({ view: 'chat' });
      setIsRestoringConversation(false);
      setIsLoadingConversation(false);
      setIsRestoringScroll(false);
      setScrollPosition(0);
      setIsStartingNewChat(true);
      setHasSeenWelcomeMessage(false); // Reset welcome message flag
      
      // Clear any stored conversation data
      if (user?.uid) {
        localStorage.removeItem(`chat_history_${user.uid}`);
      }
      
      showWelcomeMessage();
      toast.success('Chat cleared');
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      try {
        const result = await chatPersistenceService.deleteConversation(conversationId);
        
        if (result.success) {
          // Remove from local state
          setConversations(prev => prev.filter(conv => conv.conversationId !== conversationId));
          
          // If this was the current conversation, start a new one
          if (currentConversationId === conversationId) {
            handleNewChat();
          }
          
          toast.success('Conversation deleted');
        } else {
          toast.error('Failed to delete conversation: ' + result.message);
        }
      } catch (error) {
        console.error('Error deleting conversation:', error);
        toast.error('An error occurred while deleting the conversation');
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: PersonalChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // For new chats, don't specify conversationId - let backend create one
      // For existing chats, use the current conversationId
      const conversationId = currentConversationId;
      console.log('🔍 Message sending - conversationId variable:', conversationId);
      console.log('🔍 Message sending - currentConversationId state:', currentConversationId);
      
      console.log('📤 Sending message with conversation context:', {
        hasConversationId: !!conversationId,
        conversationId: conversationId,
        isStartingNewChat: isStartingNewChat,
        messagesCount: messages.length
      });

      // Get enhanced chart context for the query
      let chartContext = null;
      try {
        const chartResponse = await chartManagementService.getChartsForLLMContext(
          user?.uid || user?.id || 'JhsRHjOheDfz0lugMwXCB5uZm5o1',
          inputMessage.trim()
        );
        
        if (chartResponse.success) {
          chartContext = chartResponse.data;
          console.log('🔮 Chart context loaded for query:', {
            totalCharts: chartResponse.data.totalRelevantCharts,
            chartTypes: Object.keys(chartResponse.data.charts),
            queryAnalysis: chartResponse.data.queryAnalysis
          });
        }
      } catch (error) {
        console.warn('⚠️ Could not load chart context, continuing without it:', error);
      }

      const requestBody = {
        query: inputMessage.trim(),
        userContext: {
          userId: user?.uid || user?.id || 'JhsRHjOheDfz0lugMwXCB5uZm5o1', // Fallback to known working user ID
          birthData: user?.birthData,
          preferences: user?.preferences,
          chartContext: chartContext // Add chart context to user context
        },
        conversationHistory: messages
          .filter(msg => msg.type !== 'system') // Exclude system messages from backend conversation history
          .map(msg => {
            // Safely handle timestamp conversion
            let safeTimestamp;
            try {
              if (msg.timestamp instanceof Date) {
                safeTimestamp = msg.timestamp.toISOString();
              } else if (typeof msg.timestamp === 'string') {
                // Try to parse string timestamp
                const parsedDate = new Date(msg.timestamp);
                if (!isNaN(parsedDate.getTime())) {
                  safeTimestamp = parsedDate.toISOString();
                } else {
                  safeTimestamp = new Date().toISOString(); // Fallback to current time
                }
              } else {
                safeTimestamp = new Date().toISOString(); // Fallback to current time
              }
            } catch (error) {
              console.warn('Invalid timestamp for message:', msg.timestamp, 'using current time');
              safeTimestamp = new Date().toISOString(); // Fallback to current time
            }
            
            return {
              type: msg.type,
              content: msg.content,
              timestamp: safeTimestamp
            };
          }),
        conversationId: conversationId
      };

      console.log('Sending chat request with conversation history:', {
        query: requestBody.query,
        userId: requestBody.userContext.userId,
        userObject: user,
        totalMessages: messages.length,
        filteredHistoryLength: requestBody.conversationHistory.length,
        history: requestBody.conversationHistory
      });

              const response = await fetch(`${API_BASE_URL_WITH_VERSION}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      console.log('📡 Backend chat response:', {
        success: data.success,
        hasResponse: !!data.response,
        hasBriefAnswer: !!data.brief_answer,
        hasDetailedDescription: !!data.detailed_description,
        hasConversationId: !!data.conversationId,
        conversationId: data.conversationId,
        currentConversationId: currentConversationId,
        responseStructure: {
          response: data.response?.substring(0, 100) + '...',
          brief_answer: data.brief_answer?.substring(0, 100) + '...',
          detailed_description: data.detailed_description?.substring(0, 100) + '...'
        }
      });
      
      if (data.success) {
        // Use structured response if available, otherwise fallback to single response
        const messageContent = data.brief_answer && data.detailed_description 
          ? `${data.brief_answer}\n\n---\n\n${data.detailed_description}`
          : data.response;
          
        const assistantMessage: PersonalChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: messageContent,
          timestamp: new Date(),
          metadata: {
            brief_answer: data.brief_answer,
            detailed_description: data.detailed_description,
            confidence: data.confidence,
            sources: data.sources
          }
        };

        setMessages(prev => [...prev, assistantMessage]);
        
        // IMPORTANT: Always update conversation ID if backend returns one
        if (data.conversationId) {
          console.log('🆕 Backend returned conversation ID:', data.conversationId);
          console.log('🆕 Previous currentConversationId:', currentConversationId);
          
          // Always set the conversation ID, regardless of current state
          setCurrentConversationId(data.conversationId);
          
          // Update URL to reflect the conversation ID
          setSearchParams({ conversation: data.conversationId, view: 'chat' });
          
          // Update browser history to replace any existing conversation ID
          const newUrl = `${window.location.pathname}?conversation=${data.conversationId}&view=chat`;
          window.history.replaceState({}, '', newUrl);
          console.log('🔄 URL updated with conversation ID:', newUrl);
          
          // Force a state update to ensure conversation ID is set
          setTimeout(() => {
            console.log('🔄 Forced state update - currentConversationId should now be:', data.conversationId);
          }, 100);
        } else {
          console.warn('⚠️ Backend did not return a conversation ID');
        }
        
        // Save both messages to database
        try {
          const finalConversationId = data.conversationId || conversationId;
          
          if (finalConversationId && !finalConversationId.startsWith('temp_') && !finalConversationId.startsWith('new_')) {
            // Existing conversation - save with conversation ID
            await chatPersistenceService.saveMessageWithConversationId('user', userMessage.content, finalConversationId, {
              timestamp: userMessage.timestamp,
              userId: user?.uid
            });
            
            await chatPersistenceService.saveMessageWithConversationId('assistant', assistantMessage.content, finalConversationId, {
              timestamp: assistantMessage.timestamp,
              userId: user?.uid
            });
            
            console.log('✅ Messages saved to database with conversation ID:', finalConversationId);
          } else {
            // New conversation - the main chat endpoint already created the conversation
            // and saved the messages, so we just need to update our state
            console.log('🆕 New conversation created by main chat endpoint');
            
            if (data.conversationId) {
              setCurrentConversationId(data.conversationId);
              console.log('🆕 New conversation ID set from main response:', data.conversationId);
              
              // Update URL to reflect the new conversation
              setSearchParams({ conversation: data.conversationId, view: 'chat' });
              
              // Update browser history to replace temporary ID with real one
              const newUrl = `${window.location.pathname}?conversation=${data.conversationId}&view=chat`;
              window.history.replaceState({}, '', newUrl);
              console.log('🔄 URL updated with real conversation ID from main response:', newUrl);
            }
          }
        } catch (error) {
          console.error('❌ Failed to save messages to database:', error);
          // Fallback to localStorage
          chatPersistenceService.saveToLocalStorage([...messages, userMessage, assistantMessage], user?.uid || '');
        }
        
        // Refresh conversations list to show the new conversation
        if (!isStartingNewChat) {
          await loadConversations();
        }
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      
      // Remove the user message on error
      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const extractSummaryAndDetails = (content: string) => {
    const lines = content.split('\n');
    const summaryLines: string[] = [];
    const detailsLines: string[] = [];
    let isInDetails = false;

    for (const line of lines) {
      if (line.includes('###') || line.includes('##') || line.includes('**') || line.includes('•')) {
        isInDetails = true;
      }
      
      if (isInDetails) {
        detailsLines.push(line);
      } else {
        summaryLines.push(line);
      }
    }

    return {
      summary: summaryLines.join('\n').trim(),
      details: detailsLines.join('\n').trim()
    };
  };

  const formatAstrologyResponse = (content: string, isExpanded: boolean, metadata?: any) => {
    // If we have structured metadata, use it directly
    if (metadata?.brief_answer && metadata?.detailed_description) {
      return (
        <div className="space-y-2">
          <div className="leading-relaxed">
            <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl p-3 border border-purple-500/30 mb-2">
              <div className="flex items-center mb-2">
                <Sparkles className="w-3 h-3 mr-1 text-purple-300" />
                <span className="text-xs font-semibold text-purple-200">Brief Answer</span>
              </div>
              {renderMarkdownContent(metadata.brief_answer)}
            </div>
          </div>
          {isExpanded && (
            <div className="text-xs sm:text-sm opacity-90 border-l-2 border-purple-400/50 pl-2 sm:pl-4 mt-2">
              <div className="flex items-center mb-2">
                <Sparkles className="w-3 h-3 mr-1 text-purple-300" />
                <span className="text-xs font-semibold text-purple-200">Detailed Analysis</span>
              </div>
              {renderMarkdownContent(metadata.detailed_description)}
            </div>
          )}
        </div>
      );
    }
    
    // Fallback to the old method if no structured metadata
    const { summary, details } = extractSummaryAndDetails(content);
    
    if (!details || details.trim().length === 0) {
      return renderMarkdownContent(content);
    }

    return (
      <div className="space-y-2">
        <div className="leading-relaxed">{renderMarkdownContent(summary)}</div>
        {isExpanded && (
          <div className="text-xs sm:text-sm opacity-90 border-l-2 border-purple-400/50 pl-2 sm:pl-4 mt-2">
            {renderMarkdownContent(details)}
          </div>
        )}
      </div>
    );
  };

  const getMessageStyle = (message: PersonalChatMessage) => {
    switch (message.type) {
      case 'user':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'assistant':
        return 'bg-slate-800/90 backdrop-blur-sm text-purple-100 border border-slate-700/50';
      case 'system':
        return 'bg-blue-500/20 backdrop-blur-sm text-blue-100 border border-blue-500/30';
      default:
        return 'bg-white/10 backdrop-blur-sm text-purple-100';
    }
  };

  const formatTime = (date: Date | string) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Unknown time';
    }
    
    const now = new Date();
    const diffInHours = (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return dateObj.toLocaleDateString();
    }
  };

  // Enhanced functionality
  const handleQuickAction = async (action: QuickAction) => {
    setInputMessage(action.prompt);
    setShowQuickActions(false);
    
    // Auto-send the quick action
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const toggleVoiceInput = () => {
    if (isVoiceInputActive) {
      stopVoiceInput();
    } else {
      startVoiceInput();
    }
  };

  const startVoiceInput = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
      voiceRecognitionRef.current = new SpeechRecognition();
      
      voiceRecognitionRef.current.continuous = false;
      voiceRecognitionRef.current.interimResults = false;
      voiceRecognitionRef.current.lang = 'en-US';
      
      voiceRecognitionRef.current.onstart = () => {
        setIsVoiceInputActive(true);
        toast.success('🎤 Voice input active - speak now!');
      };
      
      voiceRecognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
        toast.success(`🎤 Heard: "${transcript}"`);
      };
      
      voiceRecognitionRef.current.onerror = (event: any) => {
        console.error('Voice recognition error:', event.error);
        setIsVoiceInputActive(false);
        toast.error('Voice input failed. Please try again.');
      };
      
      voiceRecognitionRef.current.onend = () => {
        setIsVoiceInputActive(false);
      };
      
      voiceRecognitionRef.current.start();
    } else {
      toast.error('Voice input not supported in this browser');
    }
  };

  const stopVoiceInput = () => {
    if (voiceRecognitionRef.current) {
      voiceRecognitionRef.current.stop();
      setIsVoiceInputActive(false);
    }
  };

  const handleMessageAction = (action: string, messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) return;

    switch (action) {
      case 'copy':
        navigator.clipboard.writeText(message.content);
        toast.success('Message copied to clipboard!');
        break;
      case 'share':
        if (navigator.share) {
          navigator.share({
            title: 'Astrological Insight from RRAASI',
            text: message.content,
            url: window.location.href
          });
        } else {
          navigator.clipboard.writeText(message.content);
          toast.success('Message copied to clipboard!');
        }
        break;
      case 'expand':
        toggleMessageExpansion(messageId);
        break;
    }
    setShowMessageActions(null);
  };

  const handleTyping = (value: string) => {
    setInputMessage(value);
    setIsTyping(true);
    
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
    
    setTypingTimeout(newTimeout);
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Star Animation */}
      <StarAnimation starCount={15} />
      
      <div className="relative z-10 flex flex-col h-full">
        {/* Header - Mobile Optimized */}
        <div className="bg-gradient-to-r from-purple-900/90 to-pink-900/90 backdrop-blur-md py-3 px-3 sm:px-4 border-b border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              {/* Back to History Button - Always visible for easy navigation */}
              <button
                onClick={() => {
                  setViewMode('history');
                  setSearchParams({ view: 'history' });
                }}
                className="p-2 hover:bg-purple-700/50 rounded-lg transition-colors text-white flex-shrink-0"
                title="View Chat History"
              >
                <MessageCircle className="w-5 h-5" />
              </button>
              
              {/* Browser Back Button */}
              <button
                onClick={() => window.history.back()}
                className="p-2 hover:bg-purple-700/50 rounded-lg transition-colors text-white flex-shrink-0"
                title="Go Back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              
              {/* Live Voice Chat Button */}
              <button
                onClick={() => navigate('/live-personal-chat')}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-white flex-shrink-0"
                title="Start Live Voice Chat with Rraasi"
              >
                <Mic className="w-4 h-4" />
              </button>
              
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-lg font-bold flex items-center truncate text-white">
                  <MessageCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">
                    {viewMode === 'history' ? 'Chat History' : 'Personal Astrology Chat'}
                  </span>
                </h1>
                <p className="text-xs text-purple-200 truncate">
                  {viewMode === 'history' 
                    ? `${conversations.length} conversations` 
                    : currentConversationId 
                      ? `Conversation ${currentConversationId.slice(-8)}` 
                      : 'New Chat'
                  }
                </p>
                
                {/* Chart Status Indicator - Only show in chat mode */}
                {viewMode === 'chat' && (
                  <div className="flex items-center space-x-1 mt-1">
                    {isLoadingCharts && (
                      <div className="flex items-center space-x-1 text-blue-300 text-xs">
                        <div className="w-1.5 h-1.5 bg-blue-300 rounded-full animate-pulse"></div>
                        <span>Loading charts...</span>
                      </div>
                    )}
                    {chartError && (
                      <div className="flex items-center space-x-1 text-red-300 text-xs">
                        <span>⚠️ Chart error</span>
                      </div>
                    )}
                    {!isLoadingCharts && !chartError && (
                      <div className="flex items-center space-x-1 text-green-300 text-xs">
                        <span>🔮 Charts ready</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-2 ml-2 flex-shrink-0">
              {/* Start New Chat Button - Always visible */}
              <button
                onClick={handleNewChat}
                className="px-2 sm:px-3 py-1.5 text-xs bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg transition-all duration-200 hover:scale-105 border border-green-500/30"
                title="Start New Chat"
              >
                <span className="hidden sm:inline">New Chat</span>
                <span className="sm:hidden">New</span>
              </button>
              
              {/* Clear Chat Button - Only in chat mode */}
              {viewMode === 'chat' && currentConversationId && (
                <button
                  onClick={handleClearChat}
                  className="px-2 py-1.5 text-xs text-red-400 hover:text-red-300 border border-red-400/30 hover:border-red-300/50 rounded-lg transition-colors"
                  title="Clear this conversation"
                >
                  Clear
                </button>
              )}
              
              {/* Settings Button */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 hover:bg-purple-700/50 rounded-lg transition-colors text-white"
                title="Chat settings and preferences"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Help Panel */}
        {showHelp && (
          <div className="bg-blue-500/20 backdrop-blur-sm py-3 px-4 border-b border-blue-500/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-blue-100 font-semibold text-sm flex items-center">
                <HelpCircle className="w-4 h-4 mr-2" />
                🌟 How to Use RRAASI - Your AI Astrologer
              </h3>
              <button
                onClick={() => setShowHelp(false)}
                className="text-blue-200 hover:text-white text-sm hover:bg-blue-500/20 rounded-full w-6 h-6 flex items-center justify-center transition-colors"
              >
                ×
              </button>
            </div>
            <div className="text-blue-100 text-xs space-y-2">
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                <p className="font-semibold mb-2">✨ What I Can Help You With:</p>
                <ul className="ml-3 space-y-1">
                  <li>• Personality Insights: Discover your core traits and strengths</li>
                  <li>• Career Guidance: Find your ideal professional path</li>
                  <li>• Relationship Wisdom: Understand compatibility and love</li>
                  <li>• Life Challenges: Navigate obstacles with cosmic wisdom</li>
                  <li>• Future Trends: Current planetary influences on your life</li>
                </ul>
              </div>
              
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                <p className="font-semibold mb-2">🚀 Try These Questions:</p>
                <ul className="ml-3 space-y-1">
                  <li>• "What does my birth chart reveal about my personality?"</li>
                  <li>• "Which career path is best for my astrological profile?"</li>
                  <li>• "How do current planetary transits affect me?"</li>
                  <li>• "What are my biggest strengths according to astrology?"</li>
                  <li>• "Tell me about my love life and relationships"</li>
                </ul>
              </div>
              
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                <p className="font-semibold mb-2">📊 Your Birth Data Status:</p>
                <p className="ml-3">
                  {user?.birthData 
                    ? '✅ Ready for Analysis - Your chart is fully loaded and ready for personalized insights!' 
                    : '❌ Setup Required - Please add your birth details in Profile for personalized astrological guidance'
                  }
                </p>
              </div>
              
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                <p className="font-semibold mb-2">💡 Pro Tips:</p>
                <ul className="ml-3 space-y-1">
                  <li>• Be specific with your questions for better insights</li>
                  <li>• Ask follow-up questions to dive deeper</li>
                  <li>• I can analyze multiple aspects of your life</li>
                  <li>• Your conversations are saved for future reference</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {viewMode === 'history' ? (
            /* Chat History View */
            <div className="h-full overflow-y-auto p-4">
              {/* New Chat Button */}
              <button
                onClick={handleNewChat}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-2xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center justify-center gap-3 mb-6 shadow-lg"
              >
                <Plus className="w-6 h-6" />
                <span className="text-lg font-semibold">Start New Chat</span>
              </button>

              {/* Conversations List */}
              {isLoadingConversations ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-xl font-medium mb-2">No conversations yet</p>
                  <p className="text-sm mb-6">Start your first astrology chat to see it here</p>
                  <button
                    onClick={handleNewChat}
                    className="bg-purple-500/20 text-purple-300 px-6 py-3 rounded-xl hover:bg-purple-500/30 transition-all"
                  >
                    Start Chatting
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {conversations.map((conversation) => {
                    if (!conversation.conversationId || !conversation.updatedAt) {
                      return null;
                    }
                    
                    // Format the timestamp
                    const lastMessageTime = conversation.updatedAt._seconds 
                      ? new Date(conversation.updatedAt._seconds * 1000)
                      : new Date();
                    
                    return (
                      <div
                        key={conversation.conversationId}
                        onClick={() => handleConversationSelect(conversation.conversationId)}
                        className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-4 cursor-pointer transition-all hover:bg-slate-700/70 border border-slate-700/30 hover:border-slate-600/50"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-lg truncate mb-1">
                              {conversation.title}
                            </h3>
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                Personal Chat
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(lastMessageTime)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConversation(conversation.conversationId);
                            }}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all ml-3"
                            title="Delete conversation"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              )}
            </div>
          ) : (
            /* Chat View */
            <div className="flex flex-col h-full">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 chat-messages-container pb-16">
                {messages.map(message => {
                  const isExpanded = expandedMessages.has(message.id);
                  const { details } = extractSummaryAndDetails(message.content);
                  // Check for structured metadata first, then fallback to content parsing
                  const hasDetails = (message.metadata?.detailed_description && message.metadata.detailed_description.trim().length > 0) || 
                                   (details && details.trim().length > 0);
                  
                  return (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] rounded-2xl p-3 sm:p-4 ${getMessageStyle(message)}`}>
                        {message.type === 'assistant' ? (
                          <div className="space-y-2 sm:space-y-4">
                            <div className="flex items-center mb-2 text-xs text-purple-300">
                              <Sparkles className="w-3 h-3 mr-1" />
                              Astrological Analysis
                            </div>
                            {formatAstrologyResponse(message.content, isExpanded, message.metadata)}
                            
                            {/* Show More/Less Button */}
                            {hasDetails && (
                              <button
                                onClick={() => toggleMessageExpansion(message.id)}
                                className="mt-4 text-xs text-purple-300 hover:text-purple-200 flex items-center transition-colors"
                              >
                                {isExpanded ? (
                                  <>
                                    <span>Show Less</span>
                                    <svg className="w-3 h-3 ml-1 transform rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </>
                                ) : (
                                  <>
                                    <span>Show Detailed Analysis</span>
                                    <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="leading-relaxed">
                            <div className="whitespace-pre-wrap">{message.content}</div>
                          </div>
                        )}
                        <div className="text-xs opacity-75 mt-4 flex items-center justify-between border-t border-white/10 pt-2">
                          <span>{message.timestamp.toLocaleTimeString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white/10 backdrop-blur-sm text-purple-100 rounded-2xl p-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        <span className="text-sm ml-2">Analyzing your chart...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="bg-purple-800/50 backdrop-blur-sm py-3 px-3 border-t border-white/20 mb-16">
                {/* Collapsible Quick Actions Section */}
                {showQuickActions && messages.length <= 1 && (
                  <div className="mb-3 transition-all duration-300">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-center flex-1">
                        <h3 className="text-purple-200 text-sm font-medium mb-1">🚀 Quick Start Actions</h3>
                        <p className="text-purple-300 text-xs">Tap any button for instant insights</p>
                      </div>
                      <button
                        onClick={() => setShowQuickActions(false)}
                        className="p-1 text-purple-300 hover:text-white hover:bg-purple-600/20 rounded-full transition-colors"
                        title="Hide Quick Actions"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {quickActions.map((action) => (
                        <button
                          key={action.id}
                          onClick={() => handleQuickAction(action)}
                          className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/30 hover:to-pink-600/30 border border-purple-500/30 hover:border-purple-400/50 rounded-xl p-2 text-left transition-all duration-200 hover:scale-105 group"
                        >
                          <div className="text-lg mb-1">{action.icon}</div>
                          <div className="text-white text-sm font-medium mb-1 leading-tight">{action.title}</div>
                          <div className="text-purple-200 text-xs opacity-80 group-hover:opacity-100 leading-tight">
                            {action.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Compact Input Row */}
                <div className="flex items-end space-x-2">
                  <div className="flex-1 relative">
                    <textarea
                      ref={inputRef}
                      value={inputMessage}
                      onChange={(e) => handleTyping(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="🌟 Ask me about your personality, career, love life, or any astrological insights..."
                      className="w-full bg-slate-800/50 backdrop-blur-sm text-white placeholder-gray-400 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all text-sm"
                      rows={1}
                      style={{
                        minHeight: '44px',
                        maxHeight: '100px'
                      }}
                    />
                    {/* Compact Typing indicator */}
                    {isTyping && (
                      <div className="absolute -top-4 left-0 text-xs text-purple-300 flex items-center">
                        <div className="flex space-x-1 mr-1.5">
                          <div className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" />
                          <div className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                          <div className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                        </div>
                        Typing...
                      </div>
                    )}
                  </div>
                  
                  {/* Compact Action Buttons */}
                  <div className="flex space-x-1">
                    {/* Voice Input Button */}
                    <button
                      onClick={toggleVoiceInput}
                      className={`p-2.5 rounded-xl transition-all flex-shrink-0 shadow-lg ${
                        isVoiceInputActive 
                          ? 'bg-red-500 hover:bg-red-600 text-white' 
                          : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white'
                      }`}
                      title={isVoiceInputActive ? 'Stop voice input' : 'Start voice input'}
                    >
                      {isVoiceInputActive ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                    
                    {/* Send Button */}
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputMessage.trim() || isLoading}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2.5 rounded-xl hover:from-purple-600 hover:to-pink-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed transition-all flex-shrink-0 shadow-lg"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Floating Quick Help Button */}
                <div className="absolute -top-10 right-0">
                  <button
                    onClick={() => setShowHelp(!showHelp)}
                    className="p-2 bg-gradient-to-r from-blue-500/80 to-purple-500/80 hover:from-blue-600/90 hover:to-purple-600/90 backdrop-blur-sm rounded-full shadow-lg border border-white/20 transition-all duration-200 hover:scale-110"
                    title="Quick Help & Tips"
                  >
                    <HelpCircle className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Smart Floating Navigation - Mobile Optimized */}
        <div className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 transition-all duration-300 z-30 ${
          showQuickActions && messages.length <= 1 ? 'bottom-32' : 'bottom-4'
        }`}>
          <div className="bg-gradient-to-r from-purple-800/90 to-pink-800/90 backdrop-blur-md rounded-full shadow-2xl border border-white/20 p-2">
            <div className="flex items-center space-x-2">
              {/* New Chat Button - Mobile Optimized */}
              <button
                onClick={() => {
                  setViewMode('history');
                  setSearchParams({ view: 'history' });
                }}
                className="p-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-full transition-all duration-200 hover:scale-110 shadow-lg"
                title="View Chat History & Start New Chat"
              >
                <MessageCircle className="w-5 h-5" />
              </button>

              {/* Quick Actions Toggle */}
              <button
                onClick={() => setShowQuickActions(!showQuickActions)}
                className={`p-3 rounded-full transition-all duration-200 hover:scale-110 shadow-lg ${
                  showQuickActions 
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' 
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                } text-white`}
                title={showQuickActions ? "Hide Quick Actions" : "Show Quick Actions"}
              >
                {showQuickActions ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              </button>

              {/* Settings Quick Access */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-3 rounded-full transition-all duration-200 hover:scale-110 shadow-lg ${
                  showSettings 
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700' 
                    : 'bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700'
                } text-white`}
                title="Quick Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile-Optimized Quick Actions Panel */}
        {showQuickActions && (
          <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-800/95 to-pink-800/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-4 z-20 max-w-sm w-full mx-4">
            <div className="space-y-3">
              <h3 className="text-white font-semibold text-center text-sm mb-3">Quick Actions</h3>
              
              {/* Start New Chat */}
              <button
                onClick={handleNewChat}
                className="w-full p-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Start New Chat</span>
              </button>
              
              {/* View History */}
              <button
                onClick={() => {
                  setViewMode('history');
                  setSearchParams({ view: 'history' });
                  setShowQuickActions(false);
                }}
                className="w-full p-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <MessageCircle className="w-4 h-4" />
                <span>View Chat History</span>
              </button>
              
              {/* Help */}
              <button
                onClick={() => {
                  setShowHelp(!showHelp);
                  setShowQuickActions(false);
                }}
                className="w-full p-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Help & Tips</span>
              </button>
            </div>
          </div>
        )}

        {/* Smart Space-Saving Features */}
        <div className="fixed top-4 right-4 z-20 space-y-2">
          {/* Compact Status Indicator */}
          <div className="bg-black/20 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/10">
            <div className="flex items-center space-x-2 text-xs text-white">
              <div className={`w-2 h-2 rounded-full ${user?.birthData ? 'bg-green-400' : 'bg-red-400'}`} />
              <span className="hidden sm:inline">
                {user?.birthData ? 'Chart Ready' : 'Setup Required'}
              </span>
            </div>
          </div>

          {/* Compact Help Toggle */}
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-2 bg-black/20 backdrop-blur-sm hover:bg-black/40 rounded-full border border-white/10 transition-colors"
            title="Quick Help"
          >
            <HelpCircle className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Clean Floating Action Button for New Chat - Only when not showing quick actions */}
        {viewMode === 'chat' && !showQuickActions && (
          <button
            onClick={() => {
              setViewMode('history');
              setSearchParams({ view: 'history' });
            }}
            className="fixed bottom-20 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 rounded-full shadow-lg hover:from-purple-600 hover:to-pink-600 transition-all z-20"
            title="View Chat History"
          >
            <MessageCircle className="w-5 h-5" />
          </button>
        )}

        {/* Minimal Bottom Navigation - Only for main app navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-900/95 to-pink-900/95 backdrop-blur-md border-t border-white/20 z-40">
          <div className="flex items-center justify-around py-2 px-4">
            {/* Home */}
            <button
              onClick={() => navigate('/')}
              className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-5 h-5 text-white">🏠</div>
              <span className="text-xs text-white">Home</span>
            </button>

            {/* Astrology - Current Active */}
            <button className="flex flex-col items-center space-y-1 p-2 rounded-lg bg-white/20 transition-colors">
              <div className="w-5 h-5 text-white">⭐</div>
              <span className="text-xs text-white font-medium">Astrology</span>
            </button>

            {/* Relationships */}
            <button
              onClick={() => navigate('/relationships')}
              className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-5 h-5 text-white">❤️</div>
              <span className="text-xs text-white">Relationships</span>
            </button>

            {/* Matchmaking */}
            <button
              onClick={() => navigate('/matchmaking')}
              className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-5 h-5 text-white">🔗</div>
              <span className="text-xs text-white">Matchmaking</span>
            </button>

            {/* More */}
            <button
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-5 h-5 text-white">⋯</div>
              <span className="text-xs text-white">More</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalChat; 